import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiArrowLeft,
  FiUserPlus,
  FiEdit2,
  FiTrash2,
  FiKey,
  FiX,
  FiRefreshCw,
  FiCopy,
  FiCheckCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiInfo,
} from "react-icons/fi";
import {
  fetchUsers as apiFetchUsers,
  createUser as apiCreateUser,
  deleteUser as apiDeleteUser,
  resetUserPassword as apiResetPassword,
  updateUser as apiUpdateUser,
  extractError,
} from "../services/adminService";
import "./AdminUsersPage.css";

/* ── Toast Notification Hook ── */
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message, type = "success", duration = 4000) => {
    const id = ++counterRef.current;
    setToasts((prev) => [...prev, { id, message, type, removing: false }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, removing: true } : t)));
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 350);
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, removing: true } : t)));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 350);
  }, []);

  return { toasts, addToast, removeToast };
}

const TOAST_ICONS = {
  success: <FiCheckCircle />,
  error: <FiAlertCircle />,
  warning: <FiAlertTriangle />,
  info: <FiInfo />,
};

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div className="admin-toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`admin-toast admin-toast-${t.type}${t.removing ? " admin-toast-exit" : ""}`}>
          <span className="admin-toast-icon">{TOAST_ICONS[t.type] || TOAST_ICONS.info}</span>
          <span className="admin-toast-msg">{t.message}</span>
          <button className="admin-toast-close" onClick={() => onDismiss(t.id)}><FiX /></button>
        </div>
      ))}
    </div>
  );
}

/* ── Confirm Dialog Component ── */
function ConfirmDialog({ open, title, message, confirmLabel, confirmDanger, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="admin-modal-overlay admin-confirm-overlay" onClick={onCancel}>
      <div className="admin-confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="admin-confirm-icon-wrap">
          {confirmDanger
            ? <FiAlertTriangle className="admin-confirm-icon danger" />
            : <FiInfo className="admin-confirm-icon info" />
          }
        </div>
        <h3 className="admin-confirm-title">{title}</h3>
        <p className="admin-confirm-message">{message}</p>
        <div className="admin-confirm-actions">
          <button className="admin-btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className={`admin-btn-primary${confirmDanger ? " admin-btn-danger" : ""}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Processing…" : (confirmLabel || "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminUsersPage({ onBackToHome }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Toasts & confirm dialog
  const { toasts, addToast, removeToast } = useToasts();
  const [confirmState, setConfirmState] = useState({ open: false, title: "", message: "", confirmLabel: "", confirmDanger: false, onConfirm: null, loading: false });

  // Create user form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ first_name: "", last_name: "", email: "", password: "", roles: ["user"] });
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [createdLoginId, setCreatedLoginId] = useState("");

  // Reset password
  const [resetTarget, setResetTarget] = useState(null); // { user_id, username }
  const [resetPassword, setResetPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Edit user
  const [editTarget, setEditTarget] = useState(null); // { user_id, username }
  const [editData, setEditData] = useState({ email: "", roles: [], is_active: true });
  const [editLoading, setEditLoading] = useState(false);

  const closeConfirm = () => setConfirmState((s) => ({ ...s, open: false, loading: false }));

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetchUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Create User ──
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreateLoading(true);
    setTempPassword("");
    setCreatedLoginId("");
    try {
      const data = await apiCreateUser(newUser);
      if (data.temporary_password || data.login_id) {
        setTempPassword(data.temporary_password || "");
        setCreatedLoginId(data.login_id || data.username || "");
      } else {
        setShowCreateForm(false);
        setNewUser({ first_name: "", last_name: "", email: "", password: "", roles: ["user"] });
        addToast("User created successfully!", "success");
      }
      fetchUsers();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Delete (deactivate) User ──
  const handleDelete = (user) => {
    setConfirmState({
      open: true,
      title: "Deactivate User",
      message: `Are you sure you want to deactivate "${user.login_id || user.username}"? This user will no longer be able to log in.`,
      confirmLabel: "Deactivate",
      confirmDanger: true,
      loading: false,
      onConfirm: async () => {
        setConfirmState((s) => ({ ...s, loading: true }));
        try {
          await apiDeleteUser(user.user_id);
          closeConfirm();
          addToast(`User "${user.login_id || user.username}" has been deactivated.`, "success");
          fetchUsers();
        } catch (err) {
          closeConfirm();
          addToast(`Failed to deactivate user: ${err.message}`, "error");
        }
      },
    });
  };

  // ── Reset Password ──
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetLoading(true);
    try {
      await apiResetPassword(resetTarget.user_id, resetPassword);
      const targetName = resetTarget.username;
      setResetTarget(null);
      setResetPassword("");
      addToast(`Password for "${targetName}" has been reset successfully.`, "success");
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  // ── Edit User ──
  const openEdit = (user) => {
    setEditTarget({ user_id: user.user_id, username: user.username });
    setEditData({
      email: user.email || "",
      roles: user.roles || ["user"],
      is_active: user.is_active !== false,
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await apiUpdateUser(editTarget.user_id, editData);
      const targetName = editTarget.username;
      setEditTarget(null);
      addToast(`User "${targetName}" updated successfully.`, "success");
      fetchUsers();
    } catch (err) {
      addToast(`Failed to update user: ${err.message}`, "error");
    } finally {
      setEditLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <div className="admin-users-page">
      {/* ── Toast Notifications ── */}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      {/* ── Confirm Dialog ── */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        confirmDanger={confirmState.confirmDanger}
        loading={confirmState.loading}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />

      <div className="admin-users-header">
        <button className="admin-back-btn" onClick={onBackToHome}>
          <FiArrowLeft /> Back
        </button>
        <h1 className="admin-title">User Management</h1>
        <div className="admin-header-actions">
          <button className="admin-refresh-btn" onClick={fetchUsers} disabled={loading}>
            <FiRefreshCw className={loading ? "spin" : ""} />
          </button>
          <button className="admin-create-btn" onClick={() => { setShowCreateForm(true); setTempPassword(""); setCreatedLoginId(""); setCreateError(""); }}>
            <FiUserPlus /> New User
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {/* ── Create User Modal ── */}
      {showCreateForm && (
        <div className="admin-modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Create New User</h2>
              <button className="admin-modal-close" onClick={() => setShowCreateForm(false)}><FiX /></button>
            </div>

            {tempPassword || createdLoginId ? (
              <div className="admin-temp-password">
                <p>User created successfully!</p>
                {createdLoginId && (
                  <div style={{marginBottom: '12px'}}>
                    <strong>Login ID:</strong>
                    <div className="admin-temp-value">
                      <code>{createdLoginId}</code>
                      <button onClick={() => copyToClipboard(createdLoginId)} title="Copy"><FiCopy /></button>
                    </div>
                    <p className="admin-temp-note">The user will use this ID to log in.</p>
                  </div>
                )}
                {tempPassword && (
                  <div>
                    <strong>Temporary Password:</strong>
                    <div className="admin-temp-value">
                      <code>{tempPassword}</code>
                      <button onClick={() => copyToClipboard(tempPassword)} title="Copy"><FiCopy /></button>
                    </div>
                    <p className="admin-temp-note">User must change this on first login.</p>
                  </div>
                )}
                <button className="admin-btn-primary" onClick={() => { setShowCreateForm(false); setTempPassword(""); setCreatedLoginId(""); setNewUser({ first_name: "", last_name: "", email: "", password: "", roles: ["user"] }); addToast("User created successfully!", "success"); }}>Done</button>
              </div>
            ) : (
              <form className="admin-form" onSubmit={handleCreate}>
                <label>First Name</label>
                <input type="text" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} placeholder="e.g. John" required minLength={1} />

                <label>Last Name</label>
                <input type="text" value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} placeholder="e.g. Kif" required minLength={1} />

                <label>Email</label>
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@accenture.com" required />

                <label>Password <span className="admin-optional">(leave blank to auto-generate)</span></label>
                <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 12 chars, upper/lower/digit/special" minLength={12} />

                <label>Role</label>
                <select value={newUser.roles[0]} onChange={(e) => setNewUser({ ...newUser, roles: [e.target.value] })}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>

                {createError && <p className="admin-form-error">{createError}</p>}

                <div className="admin-form-actions">
                  <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
                  <button type="submit" className="admin-btn-primary" disabled={createLoading}>
                    {createLoading ? "Creating…" : "Create User"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {resetTarget && (
        <div className="admin-modal-overlay" onClick={() => setResetTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Reset Password — {resetTarget.username}</h2>
              <button className="admin-modal-close" onClick={() => setResetTarget(null)}><FiX /></button>
            </div>
            <form className="admin-form" onSubmit={handleResetPassword}>
              <label>New Password</label>
              <input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Min 12 chars, upper/lower/digit/special" required minLength={12} />
              {resetError && <p className="admin-form-error">{resetError}</p>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setResetTarget(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={resetLoading}>
                  {resetLoading ? "Resetting…" : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit User Modal ── */}
      {editTarget && (
        <div className="admin-modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Edit User — {editTarget.username}</h2>
              <button className="admin-modal-close" onClick={() => setEditTarget(null)}><FiX /></button>
            </div>
            <form className="admin-form" onSubmit={handleEdit}>
              <label>Email</label>
              <input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />

              <label>Role</label>
              <select value={editData.roles[0] || "user"} onChange={(e) => setEditData({ ...editData, roles: [e.target.value] })}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>

              <label className="admin-checkbox-label">
                <input type="checkbox" checked={editData.is_active} onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })} />
                Active
              </label>

              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={editLoading}>
                  {editLoading ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Users Table ── */}
      {loading ? (
        <div className="admin-loading">Loading users…</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Login ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={7} className="admin-empty">No users found</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id || user.username} className={!user.is_active ? "admin-row-inactive" : ""}>
                    <td className="admin-cell-username">
                      {user.login_id || user.username}
                      {user.must_reset_password && <span className="admin-badge-reset" title="Must reset password">reset</span>}
                    </td>
                    <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>{user.email || "—"}</td>
                    <td>
                      <span className={`admin-badge-role ${user.roles?.includes("admin") ? "admin" : "user"}`}>
                        {user.roles?.join(", ") || "user"}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge-status ${user.is_active ? "active" : "inactive"}`}>
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="admin-cell-date">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : "Never"}
                    </td>
                    <td className="admin-cell-actions">
                      <button className="admin-action-btn" onClick={() => openEdit(user)} title="Edit"><FiEdit2 /></button>
                      <button className="admin-action-btn" onClick={() => { setResetTarget({ user_id: user.user_id, username: user.username }); setResetPassword(""); setResetError(""); }} title="Reset Password"><FiKey /></button>
                      {user.username !== "admin" && (
                        <button className="admin-action-btn danger" onClick={() => handleDelete(user)} title="Deactivate"><FiTrash2 /></button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminUsersPage;
